import React from "react";
import i18next from "src/i18n/config";

import { FileContentType, FileTableItem } from "@dashboard/files/types";

import { FileExtendedTableItem } from "../UploadTable.utils";

function isAndroidApkContentType(contentType: string | null): boolean {
    if (!contentType) {
        return false;
    }
    return contentType === FileContentType.Apk || contentType === FileContentType.ApkV2;
}

function hasAndroidApkDuplicate(item: FileTableItem, candidates: FileTableItem[]): boolean {
    const packageName = item.metadata?.file?.apk?.manifest?.package;
    const versionCode = item.metadata?.file?.apk?.manifest?.versioncode;

    if (!packageName || !versionCode) {
        return false;
    }

    return candidates.some(
        (file) =>
            file.id !== item.id &&
            file.metadata?.file?.apk?.manifest?.package === packageName &&
            file.metadata?.file?.apk?.manifest?.versioncode === versionCode,
    );
}

function hasNonAndroidApkDuplicate(item: FileTableItem, candidates: FileTableItem[]): boolean {
    return candidates.some((file) => {
        if (file.id === item.id || file.filename !== item.filename) {
            return false;
        }
        if (isAndroidApkContentType(file.contentType)) {
            return false;
        }
        if (item.version || file.version) {
            return item.version === file.version;
        }
        return true;
    });
}

function indexFilesBySource(arr: FileTableItem[]) {
    return arr.reduce<Record<string, FileTableItem[]>>((acc, file) => {
        const sourceKey = file.groupId !== null ? `group_${file.groupId}` : `workspace_${file.workspaceId}`;
        if (!acc[sourceKey]) {
            acc[sourceKey] = [];
        }
        acc[sourceKey].push(file);
        return acc;
    }, {});
}

function getLevelFilesMap(arr: FileTableItem[]) {
    const workspaceLevelFiles: FileTableItem[] = [];
    const groupLevelFiles: FileTableItem[] = [];

    arr.forEach((file) => {
        if (file.groupId !== null) {
            groupLevelFiles.push(file);
        } else {
            workspaceLevelFiles.push(file);
        }
    });
    return { workspaceLevelFiles, groupLevelFiles };
}

export function getAnalyzedFiles(arr: FileTableItem[]): FileExtendedTableItem[] {
    const filesBySource = indexFilesBySource(arr);
    const { workspaceLevelFiles, groupLevelFiles } = getLevelFilesMap(arr);

    return arr.map((item) => {
        const warnings = [];
        const sourceKey = item.groupId !== null ? `group_${item.groupId}` : `workspace_${item.workspaceId}`;
        const crossLevelFiles = item.groupId !== null ? workspaceLevelFiles : groupLevelFiles;

        const sameSourceFiles = filesBySource[sourceKey] || [];

        const isAndroidApk = isAndroidApkContentType(item.contentType);
        const checkForDuplicate = isAndroidApk ? hasAndroidApkDuplicate : hasNonAndroidApkDuplicate;

        if (checkForDuplicate(item, sameSourceFiles)) {
            warnings.push(i18next.t("pages:filesPage.tooltips.duplicateFile"));
        }
        if (checkForDuplicate(item, crossLevelFiles)) {
            warnings.push(i18next.t("pages:filesPage.tooltips.duplicateFileAcrossLevels"));
        }

        return { ...item, warnings };
    });
}

export function useFilesWarnings(files: FileTableItem[]): FileExtendedTableItem[] {
    return React.useMemo(() => getAnalyzedFiles(files), [files]);
}
